import { useRef } from 'react';
import {
  autoUpdateSelector,
  showStationSuggestionSelector,
  selectedLocationsSelector,
  localeSelector,
  departureCitySelector,
  arrivalCitySelector,
} from 'app/store/selectors';
import { updateSearchLocation } from 'app/store/thunk/location';
import { mapAutocompleteToLocation } from 'app/functions';
import { emitFieldChangedEvent, emitFieldClosedEvent, emitSelectCityEvent } from 'app/events';
import {
  AutocompleteResult,
  AutocompleteResultStation,
  SearchMaskLocation,
  selectedCityType,
} from 'app/models/types/store/location';
import { FIELD_ARRIVAL_CITY, FIELD_DEPARTURE_CITY } from 'app/storage/fieldNames';
import { ResultsType } from 'app/hooks/useResults';
import { emitSelectStationEvent } from 'app/events';
import { useOutsideClick } from 'app/hooks/useClickOutside';
import { getIsGlobalTripSearch } from '@webc/meeseeks-ui-sdk';
import { useAppDispatch, useAppSelector } from 'app/hooks/appHooks';
import { redirectToGlobal } from 'app/store/thunk/general/redirectToGlobalThunk';
import { ListBoxOptionType } from '@flixbus/honeycomb-react';

type AutocompleteProps = {
  direction: 'to' | 'from';
  isAlwaysExpanded: boolean;
  setIsFocused: (value: boolean) => void;
  setValue: (value: string) => void;
  citiesById: Record<
    string,
    AutocompleteResult & { positionOnList: number; station: AutocompleteResultStation }
  >;
  afterSelectAction?: () => void;
  value: string;
  resultsType: ResultsType;
};
export const useAutocomplete = ({
  direction,
  isAlwaysExpanded,
  setIsFocused,
  setValue,
  citiesById,
  afterSelectAction,
  value,
  resultsType,
}: AutocompleteProps) => {
  const dispatch = useAppDispatch();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const selected = useAppSelector(selectedLocationsSelector)[direction];
  const areStationSuggestionsAvailable = useAppSelector(showStationSuggestionSelector);
  const isAutoUpdate = useAppSelector(autoUpdateSelector);
  const selectedLocations = useAppSelector(selectedLocationsSelector);
  const locale = useAppSelector(localeSelector);
  const departureCity = useAppSelector(departureCitySelector) as SearchMaskLocation;
  const arrivalCity = useAppSelector(arrivalCitySelector) as SearchMaskLocation;

  const onHide = () => {
    if (isAlwaysExpanded) return null;
    setValue(selected.stationName || selected.name || '');
    setIsFocused(false);
  };

  useOutsideClick(wrapperRef, () => {
    onHide();
  });

  const onSelect = (city: ListBoxOptionType) => {
    const previousLocations = { arrivalCity, departureCity };

    if (city.value) {
      const cityData = citiesById[city.value];
      const selectedCity: selectedCityType =
        cityData.isStation && cityData.cityId
          ? {
              ...citiesById[cityData.cityId],
              stationId: city.value.toString(),
              stationName: city.title,
            }
          : cityData;
      const location = mapAutocompleteToLocation(selectedCity) as SearchMaskLocation;
      const secondLocation = direction === 'from' ? selectedLocations.to : selectedLocations.from;

      const isGlobalTripSearch = getIsGlobalTripSearch({
        locale,
        departureCountryCode:
          direction === 'from' ? location.countryCode : secondLocation.countryCode,
        arrivalCountryCode: direction === 'to' ? location.countryCode : secondLocation.countryCode,
      });

      if (isGlobalTripSearch && isAutoUpdate) {
        dispatch(redirectToGlobal({ direction, selectedCity: location }));
        setValue(selectedCity.name);
        return null;
      }
      dispatch(updateSearchLocation({ direction, location, previousLocations, isAutoUpdate }));
      emitFieldChangedEvent(
        direction === 'from' ? 'location.departure' : 'location.arrival',
        city.title,
      );
      setValue(city.title);

      if (areStationSuggestionsAvailable) {
        emitSelectStationEvent({
          isStationClicked: !!cityData.isStation,
          numberOfAvailableStationsInCity: selectedCity.stations?.length || 0,
          isTrainStation: cityData.station?.is_train,
        });
      }

      emitSelectCityEvent({
        direction,
        isFlixbusCity: selectedCity.isFlixbusCity,
        selectedCityId: selectedCity.id,
        selectedCityName: selectedCity.name,
        country: selectedCity.country,
        searchedTerm: value,
        positionFromTop: cityData.positionOnList,
        isStation: !!cityData.isStation,
        isPopular: resultsType === 'popular',
        isRecent: resultsType === 'recent',
        selectedStationName: selectedCity.stationName,
      });

      if (afterSelectAction) {
        afterSelectAction();
      }
      emitFieldClosedEvent(direction === 'from' ? FIELD_DEPARTURE_CITY : FIELD_ARRIVAL_CITY);
      setIsFocused(false);
    }
  };

  return {
    selected,
    onSelect,
    wrapperRef,
  };
};
